import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { NumericInput } from "../ui/numeric-input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Checkbox } from "../ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import AddressFields from "../shared/AddressFields";
import { formatPhoneNumber, formatDateInput, mmddyyyyToIso, calculateAge } from "../../lib/formatters";

const EmployeeFormDialog = ({
  open,
  onOpenChange,
  editingEmployee,
  form,
  setForm,
  employeeRoles,
  onSubmit,
}) => {
  const dobIso = mmddyyyyToIso(form.date_of_birth);
  const age = calculateAge(dobIso);
  const dobInvalid = !!form.date_of_birth && form.date_of_birth.length === 10 && !dobIso;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingEmployee ? "Edit Employee" : "Add New Employee"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="form-group">
              <Label htmlFor="first_name">First Name *</Label>
              <Input
                id="first_name"
                autoComplete="given-name"
                value={form.first_name || ""}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                required
                placeholder="John"
                data-testid="employee-first-name-input"
              />
            </div>
            <div className="form-group">
              <Label htmlFor="last_name">Last Name *</Label>
              <Input
                id="last_name"
                autoComplete="family-name"
                value={form.last_name || ""}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                required
                placeholder="Smith"
                data-testid="employee-last-name-input"
              />
            </div>
          </div>
          <div className="form-group">
            <Label htmlFor="role">Role/Position *</Label>
            {employeeRoles.length > 0 ? (
              <Select
                value={form.role}
                onValueChange={(v) => setForm({ ...form, role: v })}
              >
                <SelectTrigger data-testid="employee-role-select">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {[...employeeRoles].sort((a, b) => a.localeCompare(b)).map((role) => (
                    <SelectItem key={role} value={role}>{role}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                id="role"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                required
                placeholder="e.g., Mason, Laborer, Foreman"
                data-testid="employee-role-input"
              />
            )}
          </div>
          
          {/* Field / Office Checkboxes */}
          <div className="p-4 bg-muted/30 rounded-lg space-y-3">
            <Label className="font-medium">Employee Type</Label>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="is-office"
                  checked={form.is_office || false}
                  onCheckedChange={(checked) =>
                    setForm({ ...form, is_office: !!checked, is_field: false, base_access: null })
                  }
                  data-testid="employee-is-office"
                />
                <Label htmlFor="is-office" className="font-normal cursor-pointer">Office</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="is-field-base"
                  checked={!!form.is_field && form.base_access === true}
                  onCheckedChange={(checked) =>
                    setForm({ ...form, is_office: false, is_field: !!checked, base_access: checked ? true : null })
                  }
                  data-testid="employee-is-field-base-access"
                />
                <Label htmlFor="is-field-base" className="font-normal cursor-pointer">Field (Base Access)</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="is-field-no-base"
                  checked={!!form.is_field && form.base_access !== true}
                  onCheckedChange={(checked) =>
                    setForm({ ...form, is_office: false, is_field: !!checked, base_access: checked ? false : null })
                  }
                  data-testid="employee-is-field-no-base-access"
                />
                <Label htmlFor="is-field-no-base" className="font-normal cursor-pointer">Field (No Base Access)</Label>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Pick one. Field employees are visible to Foremen; Base Access marks who is cleared for military base jobs.
            </p>
          </div>

          {/* Hourly Rate - Required only for Field employees */}
          {(form.is_field || !form.is_office) && (
            <div className="form-group">
              <Label htmlFor="hourly_rate">Hourly Rate ($) {form.is_field ? "*" : ""}</Label>
              <NumericInput
                id="hourly_rate"
                decimals={2}
                min={0}
                value={form.hourly_rate}
                onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })}
                placeholder="25.00"
                data-testid="employee-rate-input"
              />
            </div>
          )}
          
          {/* Address Fields - Using shared component */}
          <AddressFields
            values={{ street: form.street, apt: form.apt, city: form.city, state: form.state, zip_code: form.zip_code }}
            onChange={(field, value) => setForm({ ...form, [field]: value })}
            onChangeMany={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
            prefix="employee"
            showBorder={false}
            compact={true}
          />
          
          <div className="form-group">
            <Label htmlFor="phone">Phone</Label>
            <Input
              id="phone"
              autoComplete="tel"
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: formatPhoneNumber(e.target.value) })}
              placeholder="(555) 123-4567"
              maxLength={14}
              data-testid="employee-phone-input"
            />
          </div>
          <div className="form-group">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              autoComplete="email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="john@example.com"
              data-testid="employee-email-input"
            />
          </div>

          <div className="form-group">
            <Label htmlFor="ssn_last4">Worker ID — last 4 of SSN <span className="text-muted-foreground font-normal">(certified payroll)</span></Label>
            <Input
              id="ssn_last4"
              autoComplete="off"
              inputMode="numeric"
              maxLength={4}
              value={form.ssn_last4 || ""}
              onChange={(e) => setForm({ ...form, ssn_last4: e.target.value.replace(/\D/g, "").slice(0, 4) })}
              placeholder="1234"
              className="max-w-[120px] font-mono"
              data-testid="employee-ssn-last4-input"
            />
            <p className="text-xs text-muted-foreground mt-1">Only these 4 digits are stored. Printed in column 1E of the WH-347.</p>
          </div>

          <div className="form-group">
            <Label htmlFor="date_of_birth" className="flex items-center gap-2">
              D.O.B.
              {age != null && (
                <span className="text-muted-foreground font-normal" data-testid="employee-age">Age: {age}</span>
              )}
            </Label>
            <Input
              id="date_of_birth"
              autoComplete="off"
              inputMode="numeric"
              maxLength={10}
              value={form.date_of_birth || ""}
              onChange={(e) => setForm({ ...form, date_of_birth: formatDateInput(e.target.value) })}
              placeholder="MM/DD/YYYY"
              className={`max-w-[160px] font-mono ${dobInvalid ? "border-red-500" : ""}`}
              aria-invalid={dobInvalid || undefined}
              data-testid="employee-dob-input"
            />
            {dobInvalid && (
              <p className="text-xs text-red-600 mt-1" data-testid="employee-dob-error">Enter a valid date as MM/DD/YYYY.</p>
            )}
          </div>

          {/* Emergency Contact */}
          <div className="p-4 bg-muted/30 rounded-lg space-y-3">
            <Label className="font-medium">Emergency Contact</Label>
            <div className="grid grid-cols-2 gap-3">
              <Input
                value={form.emergency_contact?.name || ""}
                onChange={(e) => setForm({ ...form, emergency_contact: { ...form.emergency_contact, name: e.target.value } })}
                placeholder="Contact name"
                data-testid="employee-ec-name-input"
              />
              <Input
                value={form.emergency_contact?.relationship || ""}
                onChange={(e) => setForm({ ...form, emergency_contact: { ...form.emergency_contact, relationship: e.target.value } })}
                placeholder="Relationship (e.g., Spouse)"
                data-testid="employee-ec-relationship-input"
              />
            </div>
            <Input
              type="tel"
              value={form.emergency_contact?.phone || ""}
              onChange={(e) => setForm({ ...form, emergency_contact: { ...form.emergency_contact, phone: formatPhoneNumber(e.target.value) } })}
              placeholder="(555) 123-4567"
              maxLength={14}
              data-testid="employee-ec-phone-input"
            />
            <p className="text-xs text-muted-foreground">Filled automatically from new-hire onboarding paperwork when available.</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="form-group">
              <Label htmlFor="hire_date">Hire Date</Label>
              <Input
                id="hire_date"
                type="date"
                value={form.hire_date}
                onChange={(e) => setForm({ ...form, hire_date: e.target.value })}
                data-testid="employee-hire-date-input"
              />
            </div>
            <div className="form-group">
              <Label htmlFor="separation_date">Separation Date</Label>
              <Input
                id="separation_date"
                type="date"
                value={form.separation_date}
                onChange={(e) => setForm({ ...form, separation_date: e.target.value })}
                data-testid="employee-separation-date-input"
              />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <Label htmlFor="is_active">Active Employee</Label>
            <Switch
              id="is_active"
              checked={form.is_active}
              onCheckedChange={(checked) =>
                setForm({ ...form, is_active: checked, rehireable: checked ? null : form.rehireable })
              }
              data-testid="employee-active-switch"
            />
          </div>
          
          {/* Re-Hireable checkbox - only shown when inactive */}
          {!form.is_active && (
            <div className="p-4 bg-muted/30 rounded-lg space-y-3">
              <Label className="font-medium">Re-Hireable?</Label>
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="rehireable-yes"
                    checked={form.rehireable === true}
                    onCheckedChange={(checked) => setForm({ ...form, rehireable: checked ? true : null })}
                    data-testid="rehireable-yes"
                  />
                  <Label htmlFor="rehireable-yes" className="font-normal">Yes</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="rehireable-no"
                    checked={form.rehireable === false}
                    onCheckedChange={(checked) => setForm({ ...form, rehireable: checked ? false : null })}
                    data-testid="rehireable-no"
                  />
                  <Label htmlFor="rehireable-no" className="font-normal">No</Label>
                </div>
              </div>
            </div>
          )}
          
          <Button type="submit" className="w-full" data-testid="submit-employee-btn">
            {editingEmployee ? "Update Employee" : "Add Employee"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EmployeeFormDialog;
